// Single source of truth for mobile-number country handling (see
// docs/bahrain-readiness.md).  Customer mobiles are stored as bare local
// digits plus an optional `mobile_code` dial code; legacy docs without a code
// are Kuwaiti.  Bahrain (+973) shares Kuwait's 8-digit local format, so the
// country can NEVER be inferred from the digits — always carry the code.
//
// Never prefix "+965" inline anywhere else: route every send/match through
// these helpers.

import { COUNTRIES_DIAL_CODES } from "../../webapp/src/countries_dial_codes";

export const DEFAULT_DIAL_CODE = "+965";

// Dial codes we recognize when splitting a full number back into code +
// local. GCC codes are 3 digits; Egypt (+20, KFH Egypt RMs and their clients)
// is 2 digits. None is a prefix of another, so prefix matching is unambiguous.
export const KNOWN_DIAL_CODES = ["+965", "+973", "+966", "+971", "+974", "+968", "+20"];

export const normalizeMobileDigits = (mobile: string): string => {
    let normalized = (mobile || "").replace(/[\s()-]/g, "");
    if (normalized.startsWith("00")) {
        normalized = "+" + normalized.slice(2);
    }
    return normalized;
};

/**
 * Full E.164 for a stored mobile.  Bare local digits without a code fall back
 * to +965 — identical to the historic hardcoded behavior, so callers that
 * haven't been migrated yet keep working for Kuwait.
 */
export const toE164 = (mobile: string, mobileCode?: string): string => {
    const normalized = normalizeMobileDigits(mobile);
    if (normalized.startsWith("+")) {
        return normalized;
    }
    if (mobileCode) {
        return `${mobileCode}${normalized}`;
    }
    // digits that already start with a known dial code and are longer than a
    // bare 8-digit local number (e.g. "97336001234") just need the +
    const known = KNOWN_DIAL_CODES.find((code) => normalized.startsWith(code.slice(1)) && normalized.length > 8);
    if (known) {
        return `+${normalized}`;
    }
    return `${DEFAULT_DIAL_CODE}${normalized}`;
};

export const toWhatsappAddress = (mobile: string, mobileCode?: string): string =>
    `whatsapp:${toE164(mobile, mobileCode)}`;

/**
 * Split an E.164 (or bare-digit) number into dial code + local part.
 * Unknown dial codes return the full digits as `local` with no code.
 */
// Every dial code the country pickers expose, longest first so "+1264" wins over "+1".
const ALL_DIAL_CODES: string[] = [...new Set(COUNTRIES_DIAL_CODES.map((c) => c.dial_code))]
    .sort((a, b) => b.length - a.length);

/**
 * Split an E.164 (or bare-digit) number into dial code + local part using the
 * complete dial-code dataset (same list as the pickers), so any country the
 * RM or client can select round-trips correctly (e.g. +44 7911 123456).
 * Prefers the dial code that leaves a plausible local part (4–12 digits).
 * Unknown dial codes return the full digits as `local` with no code.
 */
export const splitE164 = (number: string): { dialCode?: string, local: string } => {
    const normalized = normalizeMobileDigits(number);
    const hasPlus = normalized.startsWith("+");
    const digits = normalized.replace(/^\+/, "");
    if (!hasPlus) {
        // bare digits: only the known regional codes are safe to infer, and only when the
        // number is longer than a bare 8-digit local (so "96556789" is never misread)
        const known = KNOWN_DIAL_CODES.find((code) => digits.startsWith(code.slice(1)) && digits.length >= (code.length - 1) + 7);
        return known ? { dialCode: known, local: digits.slice(known.length - 1) } : { local: digits };
    }
    const candidates = ALL_DIAL_CODES.filter((code) => digits.startsWith(code.slice(1)));
    const match = candidates.find((code) => {
        const localLength = digits.length - (code.length - 1);
        return localLength >= 4 && localLength <= 12;
    }) ?? candidates[0];
    if (match) {
        return { dialCode: match, local: digits.slice(match.length - 1) };
    }
    return { local: digits };
};

export const EGYPT_DIAL_CODE = "+20";

/**
 * Default dial code for an RM (or a customer created by that RM) based on the
 * RM's segment. Only the "KFH Egypt" segment is Egyptian; every other segment
 * is Kuwaiti. Matching is case-insensitive on the word "egypt" so a renamed
 * segment ("KFH-Egypt", "KFH Egypt RMs") still resolves.
 */
export const dialCodeForSegment = (segment?: string | null): string =>
    /egypt/i.test(segment ?? "") ? EGYPT_DIAL_CODE : DEFAULT_DIAL_CODE;

/**
 * Normalize a user-entered mobile into the stored shape: bare local digits in
 * `mobile` plus a dial code in `mobile_code`.
 *  - "+20101234567" / "0020101234567"       -> { mobile: "101234567", mobile_code: "+20" }
 *  - "65123456" with mobileCode "+965"       -> { mobile: "65123456", mobile_code: "+965" }
 *  - "65123456" with no code                 -> { mobile: "65123456", mobile_code: fallback }
 *  - "+4471234..." (unknown code, no hint)   -> { mobile: "+4471234...", mobile_code: undefined }
 *    (toE164 still handles a "+"-prefixed mobile, so nothing is lost)
 * A `+`-prefixed input always wins over the passed `mobileCode`.
 */
export const splitMobileInput = (
    mobile: string,
    mobileCode?: string | null,
    fallbackDialCode: string = DEFAULT_DIAL_CODE,
): { mobile: string, mobile_code?: string } => {
    const normalized = normalizeMobileDigits(mobile);
    const cleanCode = (mobileCode ?? "").replace(/[^\d+]/g, "");
    const hintedCode = cleanCode ? (cleanCode.startsWith("+") ? cleanCode : `+${cleanCode}`) : undefined;

    if (normalized.startsWith("+")) {
        if (hintedCode && normalized.startsWith(hintedCode) && normalized.length > hintedCode.length) {
            return { mobile: normalized.slice(hintedCode.length), mobile_code: hintedCode };
        }
        const { dialCode, local } = splitE164(normalized);
        if (dialCode) {
            return { mobile: local, mobile_code: dialCode };
        }
        return { mobile: normalized, mobile_code: undefined };
    }

    return { mobile: normalized, mobile_code: hintedCode ?? fallbackDialCode };
};
